"use client";

import { DownloadIcon } from "@/components/ui/download";
import { FolderCheckIcon } from "@/components/ui/folder-check";
import { FolderPlusIcon } from "@/components/ui/folder-plus";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { downloadFile } from "@/lib/download-file";
import { cn } from "@/lib/utils";
import { FileIcon } from "lucide-react";
import Image from "next/image";
import { useMemo } from "react";

const OFFICE_LOGOS: Record<string, string> = {
  DOCX: "/Microsoft_Office_Word_(2025%E2%80%93present).svg",
  PPTX: "/Microsoft_Office_PowerPoint_(2025%E2%80%93present).svg",
  XLSX: "/Microsoft_Office_Excel_(2025%E2%80%93present).svg",
};

const EXT_LABEL: Record<string, string> = {
  DOCX: "Word Document", PPTX: "PowerPoint", XLSX: "Excel Spreadsheet",
  PDF: "PDF", ZIP: "Archive", CSV: "Spreadsheet",
};

const getExtension = (filename: string) => {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === filename.length - 1) return "FILE";
  return filename.slice(lastDot + 1).toUpperCase();
};

const getDisplayName = (filename: string) => {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0) return filename;
  return filename.slice(0, lastDot);
};

export const GeneratedFileRow = ({
  filename,
  url,
  className,
  isLoading = false,
  isError = false,
  onOpenInSidebar,
  onClickDownload,
  onClickPreview,
  onAddToProject,
  isAddingToProject = false,
  isAddedToProject = false,
}: {
  filename: string;
  url?: string;
  className?: string;
  isLoading?: boolean;
  isError?: boolean;
  onOpenInSidebar?: (filename: string, url: string) => void;
  onClickDownload?: () => void;
  onClickPreview?: () => void;
  /** Attaches this file to the current project and indexes it for RAG retrieval. */
  onAddToProject?: () => void;
  isAddingToProject?: boolean;
  isAddedToProject?: boolean;
}) => {
  const extension = useMemo(() => getExtension(filename), [filename]);
  const displayName = useMemo(() => getDisplayName(filename), [filename]);
  const officeLogo = OFFICE_LOGOS[extension];
  const typeLabel = EXT_LABEL[extension] ?? extension;

  const canOpen = !!(
    (isLoading || isError)
      ? (onClickPreview || onClickDownload)
      : (url ? onOpenInSidebar : onClickPreview)
  );

  const handleOpen = () => {
    if (url && onOpenInSidebar) onOpenInSidebar(filename, url);
    else onClickPreview?.();
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (url) {
      void downloadFile(url, filename);
    } else {
      onClickDownload?.();
    }
  };

  return (
    <div
      className={cn(
        "group flex w-full items-center gap-3.5 rounded-xl border border-border bg-card p-3.5 shadow-sm transition-colors",
        canOpen && "cursor-pointer hover:bg-accent/30",
        isError && "border-destructive/40",
        className,
      )}
      onClick={() => { if (canOpen) handleOpen(); }}
    >
      {/* Stacked-page icon tile */}
      <div className="relative flex-none" style={{ width: 50, height: 54 }}>
        <div
          className="absolute bg-muted/70 border border-border rounded-lg origin-bottom -rotate-[8deg] transition-transform duration-300 group-hover:-rotate-[13deg]"
          style={{ top: 5, left: 8, width: 34, height: 44 }}
        />
        <div
          className="absolute bg-background border border-border rounded-lg shadow-sm flex items-center justify-center origin-bottom rotate-[5deg] transition-transform duration-300 group-hover:rotate-[8deg] group-hover:-translate-y-px overflow-hidden"
          style={{ top: 5, left: 8, width: 34, height: 44 }}
        >
          {officeLogo ? (
            <Image alt={`${extension} logo`} className="size-6 object-contain" height={24} src={officeLogo} width={24} />
          ) : (
            <FileIcon size={17} className="text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Meta */}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span className="font-semibold text-base text-foreground truncate tracking-tight leading-tight">
          {displayName}
        </span>
        <span className={cn("text-sm", isError ? "text-destructive" : "text-muted-foreground")}>
          {isError ? "Failed to load" : `Doc · ${typeLabel}`}
        </span>
      </div>

      {/* Add to project */}
      {onAddToProject && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={isAddedToProject ? "Added to project" : "Add to project"}
                className={cn(
                  "flex-none flex size-9 items-center justify-center rounded-[10px] border border-border bg-background text-muted-foreground transition-colors",
                  isAddedToProject ? "text-primary" : "hover:bg-muted hover:text-foreground",
                  isAddingToProject && "opacity-60 pointer-events-none",
                )}
                type="button"
                disabled={isAddingToProject || isAddedToProject}
                onClick={(e) => { e.stopPropagation(); onAddToProject(); }}
              >
                {isAddedToProject ? (
                  <FolderCheckIcon size={16} />
                ) : (
                  <FolderPlusIcon size={16} />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {isAddedToProject ? "Added to project" : "Add to project"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Ghost download button */}
      <button
        className={cn(
          "flex-none flex items-center gap-1.5 text-sm font-medium text-foreground bg-background border border-border rounded-[10px] px-3.5 py-2 transition-colors",
          (url || onClickDownload) ? "hover:bg-muted" : "opacity-40 pointer-events-none",
        )}
        type="button"
        disabled={!url && !onClickDownload}
        onClick={handleDownload}
      >
        <DownloadIcon size={15} /> Download
      </button>
    </div>
  );
};
